/**
 * What an offer is made of and what it comes to: the priced lines, the terms they are priced on,
 * and the total.
 *
 * WHY THIS PANE EXISTS. Phase 6 shipped quoting in June and the product never grew a surface for
 * it: a prepared quote has been openable since the classification was registered, showing its
 * number, its revision and its approvals, and NOTHING of the offer itself. A quote nobody can
 * price is the most visible hole in this product, and it is this pane.
 *
 * THE TERMS ARE HERE AND NOT ON THE DETAIL TAB, which looks like a layout choice and is not. The
 * currency, the margin and the overtime are not fields describing the record -- they are the
 * INPUTS TO THE ARITHMETIC in the panel below them, and the service computes all of it in one
 * query. Putting them on another tab would mean a person changing a margin could not see what it
 * did to the total, which is the only reason to change one. `prepared_quote_terms.py` says the same
 * thing in its own words: a form when the fields are shown, a table when they are computed with.
 *
 * AN OFFER WITH NO TERMS IS A FIRST-RUN STATE, NOT AN ERROR. A new quote has no terms row, so the
 * lines read answers 409 CC-PQTE-TERMS -- it has no currency and therefore cannot have a total.
 * This renders that as the step it is, with the control that fixes it, because every new quote in
 * the product starts there and a red error on a record somebody just created teaches them that the
 * product is broken.
 *
 * READ-ONLY OUTSIDE DRAFT, AND THE SENTENCE IS THE SERVER'S. A released offer's lines cannot be
 * changed -- `CC-PQLN-FROZEN` -- because what a released quote says is what the customer was sent.
 * The controls are hidden rather than disabled, and the notice says the recoverable thing: revise
 * the quote, which makes a draft to reprice. If this pane said less than the refusal does, a person
 * would meet the refusal having been given no way to act on it.
 *
 * THE TOTAL IS NEVER COMPUTED HERE. Not the extended amounts, not the subtotal, not the margin.
 * `totals()` in the service is THE one computation -- margin on the line subtotal, overtime added
 * after it, rounded once at the end to the currency's minor unit -- and a column of numbers this
 * component added up itself would differ from the quote the customer receives by a rounding step.
 * Every write returns the new totals for exactly this reason, so there is never a frame where a
 * line and a total disagree.
 *
 * TWO REVIEW FACTS TRAVEL BESIDE THE MONEY AND ARE SHOWN BESIDE IT: how many lines are hazardous,
 * and how many carry no cost source. Both are things somebody needs to know before an offer goes
 * out, and the service returns them because a client that summed the rows itself could not say
 * either.
 */
import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {
    Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControlLabel, IconButton, MenuItem, TextField, Tooltip, Typography,
} from "@mui/material";
import {AddOutlined, DeleteOutlined, EditOutlined, WarningAmberOutlined} from "@mui/icons-material";
import {FormObjectPicker} from "../Forms/FormObjectPicker";

import {AppContext} from "../../util/AppContext";
import {makeCresAxios} from "../../util/Axios";
import {Blue3, Grey2, Grey5, Grey6, Grey7, Grey8, Grey17} from "../../theme/styles";

// THE FOLDER'S VERB, since 2026-10-27. This read "preparedQuote:edit" until that evening -- a name retired
// that morning with the ingredients' own privileges -- so object_access never granted it and every
// quote response was read-only on screen for everybody, a defect the retirement did not announce.
const EDIT_PRIVILEGE = "project:edit";

/** Where a price came from. The service's own list -- a fifth value is refused there, not here. */
const COST_SOURCES = ["CATALOGUE", "SUPPLIER", "RESEARCH", "MANUAL"];

type Line = {
    USID: string;
    Description?: string | null;
    Quantity?: string | null;
    UnitOfMeasure?: string | null;
    UnitCost?: string | null;
    ExtendedAmount?: string | null;
    Currency?: string | null;
    Hazard?: boolean;
    HazardNote?: string | null;
    SortOrder?: number;
    CostSource?: string | null;
    ItemObjectUSID?: string | null;
    /** The material's identity and sourcing, on a LIST read only (the service fills them there). */
    ItemObjectID?: string | null;
    ItemName?: string | null;
    Sourcing?: {
        OnHand?: {Quantity?: string | null; UnitOfMeasure?: string | null; Lots?: number; Note?: string | null} | null;
        BestSupplier?: {SupplierObjectUSID?: string | null; SupplierObjectID?: string | null;
                        SupplierName?: string | null; LeadTimeDays?: number | null;
                        UnitPrice?: {Amount?: string | null; Currency?: string | null} | null;
                        Approved?: number} | null;
        LeadTimeDays?: number | null;
    } | null;
};

type Totals = {
    Currency?: string | null;
    Lines?: number;
    Subtotal?: string | null;
    MarginPercent?: string | null;
    MarginAmount?: string | null;
    OvertimeCost?: string | null;
    Total?: string | null;
    HazardousLines?: number;
    LinesWithNoCostSource?: number;
};

type Terms = {
    QuoteRequestUSID?: string | null;
    Currency?: string | null;
    ValidUntil?: string | null;
    MarginPercent?: string | null;
    OvertimeCost?: string | null;
    Notes?: string | null;
    CustomerObjectUSID?: string | null;
};

/**
 * Round a decimal STRING to a number of places, half away from zero, without ever becoming a float.
 *
 * NO Number() AND NO toFixed(). Both go through a double: `Number("11820.005").toFixed(2)` is
 * "11820.00" on a value whose true half rounds up, and a quote is a document somebody adds up by
 * hand. These amounts arrive as decimal strings from a server that did the arithmetic in Decimal,
 * and the only safe thing to do with them in a browser is treat them as text.
 *
 * HALF AWAY FROM ZERO, matching the service's own ROUND_HALF_UP -- so a line printed here and the
 * same line printed in the PDF cannot differ by a cent.
 */
export function roundDecimalString(value: string, digits: number): string {
    const negative = value.startsWith("-");
    const bare = negative ? value.slice(1) : value;
    const [whole, fraction = ""] = bare.split(".");
    if (fraction.length <= digits) {
        const padded = digits === 0 ? whole : `${whole}.${fraction.padEnd(digits, "0")}`;
        return `${negative ? "-" : ""}${padded}`;
    }
    const kept = `${whole}${fraction.slice(0, digits)}`;
    // Carry by string addition on the kept digits, so a 9...9 boundary rolls over correctly.
    const roundUp = Number(fraction[digits]) >= 5;
    let carried = kept;
    if (roundUp) {
        const digitsOut = kept.split("");
        let index = digitsOut.length - 1;
        while (index >= 0) {
            if (digitsOut[index] === "9") { digitsOut[index] = "0"; index -= 1; continue; }
            digitsOut[index] = String(Number(digitsOut[index]) + 1);
            break;
        }
        carried = (index < 0 ? "1" : "") + digitsOut.join("");
    }
    const cut = digits === 0 ? carried : carried.slice(0, carried.length - digits);
    const tail = digits === 0 ? "" : carried.slice(carried.length - digits);
    const head = cut === "" ? "0" : cut;
    return `${negative ? "-" : ""}${head}${digits === 0 ? "" : `.${tail}`}`;
}

/**
 * Money as the currency's own minor unit prints it, from the string the service sent.
 *
 * THE RENDERER IS WHAT ROUNDS, AND THAT IS THIS. `totals()` is explicit about the division of
 * labour: it rounds the TOTAL once, from an unrounded sum, and leaves every line's extended amount
 * at full precision deliberately -- because rounding each line and summing those moves the answer
 * away from the printed column. So the amounts arriving here are exact and unprinted, and showing
 * them raw puts "11,820.000000" and a unit cost of "98.5000" in front of a customer.
 *
 * THE PLACES COME FROM THE CURRENCY, not from a constant two. JPY has no minor unit at all, and a
 * yen amount shown with two decimals is wrong in the way that makes somebody distrust the whole
 * document.
 *
 * THE VALUE IS KEPT AS A STRING THROUGHOUT. Parsing it into a JavaScript number to display it is
 * how 1234.55 becomes 1234.5499999999998 on somebody's screen.
 */
export function money(value?: string | null, currency?: string | null, digits = 2): string {
    if (value === null || value === undefined || value === "") return "—";
    const rounded = roundDecimalString(String(value), digits);
    const negative = rounded.startsWith("-");
    const bare = negative ? rounded.slice(1) : rounded;
    const [whole, fraction] = bare.split(".");
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const amount = `${negative ? "-" : ""}${grouped}${fraction ? `.${fraction}` : ""}`;
    return currency ? `${amount} ${currency}` : amount;
}

/** A quantity, grouped but never rounded -- a line can be quoted in thousandths. */
function quantity(value?: string | null): string {
    if (value === null || value === undefined || value === "") return "—";
    const [whole, fraction] = String(value).split(".");
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    // TRAILING ZEROES ARE DROPPED but a real fraction is kept: the service stores 2.000 for two,
    // and a column reading "2.000" invites somebody to wonder what the precision means.
    const trimmed = (fraction ?? "").replace(/0+$/, "");
    return trimmed ? `${grouped}.${trimmed}` : grouped;
}

function when(value?: string | null): string {
    if (!value) return "—";
    const at = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(at.getTime())) return String(value).slice(0, 10);
    return at.toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"});
}

/** The columns, once. The header and every row read this so they cannot drift apart. */
/**
 * THE LINE GRID, in the source product's columns (Ed, 2026-10-28): Material, Description, Qty,
 * Unit, Unit Cost, Total, On-hand, Best Supplier, Lead Time, and the row's actions. The last
 * three are SOURCING facts the service reads per line -- the lot ledger, the approved supplier
 * list -- and each says why when it cannot answer, rather than drawing a blank.
 */
const COLUMNS = "minmax(150px, 1.6fr) minmax(160px, 2fr) 64px 60px 108px 96px 84px minmax(110px, 1fr) 72px 64px";

const headerCell: React.CSSProperties = {
    fontSize: 12, fontWeight: 600, color: Grey7, textTransform: "uppercase", letterSpacing: "0.04em",
};

export default function QuoteLinesPane(
    {revisionUSID, readOnly, status, onChanged}:
    {revisionUSID?: string | null; readOnly?: boolean; status?: string | null;
     /** Told after a line or the terms change, so a host drawing the offer's answers above
      *  this grid can read them again. */
     onChanged?: () => void}) {

    const appContext: any = useContext(AppContext);
    const sessionContext = appContext?.sessionContext;
    const api = makeCresAxios(sessionContext);

    const [lines, setLines] = useState<Line[] | null>(null);
    const [totals, setTotals] = useState<Totals | null>(null);
    const [terms, setTerms] = useState<Terms | null>(null);
    const [needsTerms, setNeedsTerms] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [mayEdit, setMayEdit] = useState(false);
    const [busy, setBusy] = useState<string | null>(null);

    // THE CURRENCIES AND UNITS COME FROM THE SESSION, not from a request. Both are pick lists the
    // platform already hands the browser at sign-in, and a fetch here would be a second definition
    // of "which currencies does this tenant know".
    const picks: any[] = useMemo(() => {
        try {
            return sessionContext?.getPickValues?.() ?? [];
        } catch {
            return [];
        }
    }, [sessionContext]);
    const currencies = useMemo(
        () => picks.filter(one => one?.Type === "Currency")
                   .map(one => String(one.Value)).sort(), [picks]);
    // HOW MANY DECIMAL PLACES THIS CURRENCY HAS, carried on the pick value's Group -- the same
    // place currencies.py reads it from, and the reason it is returned rather than dropped: it is
    // the difference between a rounding bug and a refusal. Two is the fallback for a currency the
    // list does not describe, because most currencies have two and a wrong guess shows a cent that
    // is not there rather than hiding one that is.
    const minorUnits = useMemo(() => {
        const out: Record<string, number> = {};
        picks.filter(one => one?.Type === "Currency").forEach(one => {
            const places = Number(one?.Group);
            out[String(one.Value)] = Number.isFinite(places) ? places : 2;
        });
        return out;
    }, [picks]);
    const units = useMemo(
        () => picks.filter(one => one?.Type === "Unit of Measure")
                   .map(one => String(one.Value)).sort(), [picks]);

    const editable = !readOnly && mayEdit;

    const load = useCallback((changed = false) => {
        if (!revisionUSID) return;
        if (changed) onChanged?.();
        setLoading(true);
        api.get("t1/v1/prepared_quote_lines", {params: {PreparedQuoteRevisionUSID: revisionUSID}})
            .then((response: any) => {
                const message = response?.data?.PlatformMessage ?? {};
                const held = message?.ListOfPreparedQuoteLine?.PreparedQuoteLine ?? [];
                setLines(Array.isArray(held) ? held : [held]);
                setTotals(message?.Totals ?? null);
                setNeedsTerms(false);
                setError(null);
            })
            .catch((problem: any) => {
                const code = problem?.response?.data?.Code;
                // THE FIRST-RUN STATE, TOLD APART BY ITS CODE AND NOT BY ITS STATUS. A 409 here is
                // either "no terms yet" or something else entirely, and only the code says which.
                if (code === "CC-PQTE-TERMS") {
                    setNeedsTerms(true);
                    setLines([]);
                    setTotals(null);
                    setError(null);
                    return;
                }
                setLines(null);
                setTotals(null);
                setError(problem?.response?.data?.Cause
                         ?? "This quote's lines could not be read.");
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [revisionUSID]);

    const loadTerms = useCallback(() => {
        if (!revisionUSID) return;
        api.get("t1/v1/prepared_quote_terms", {params: {PreparedQuoteRevisionUSID: revisionUSID}})
            .then((response: any) => {
                setTerms(response?.data?.PlatformMessage?.PreparedQuoteTerms
                         ?? response?.data?.PlatformMessage ?? null);
            })
            // NOT SURFACED. The lines read is what reports a missing terms row, with the service's
            // own sentence; a second error saying the same thing twice helps nobody.
            .catch(() => setTerms(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [revisionUSID]);

    useEffect(() => { load(); loadTerms(); }, [load, loadTerms]);

    // ASKED OF THE RECORD, NOT OF THE SESSION. A per-share grant or a deny narrows privileges for
    // ONE quote, so a global answer would offer an edit the server will refuse -- after the person
    // has filled the dialog in.
    useEffect(() => {
        if (!revisionUSID) return;
        let cancelled = false;
        api.get("t1/v1/object_access", {params: {USID: revisionUSID}})
            .then((response: any) => {
                if (cancelled) return;
                const granted: string[] = response?.data?.EffectivePrivileges ?? [];
                setMayEdit(granted.includes(EDIT_PRIVILEGE));
            })
            .catch(() => { if (!cancelled) setMayEdit(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [revisionUSID]);

    const [lineDialog, setLineDialog] = useState<Line | "new" | null>(null);
    const [termsDialog, setTermsDialog] = useState(false);

    const remove = async (line: Line) => {
        setBusy(line.USID);
        try {
            await api.delete("t1/v1/prepared_quote_lines", {params: {USID: line.USID}});
            load(true);
        } catch (problem: any) {
            setError(problem?.response?.data?.Cause ?? "That line could not be removed.");
        } finally {
            setBusy(null);
        }
    };

    if (loading && lines === null && !error) {
        return <Box sx={{display: "flex", gap: "10px", alignItems: "center", padding: "20px 0"}}>
            <CircularProgress size={18}/>
            <Typography sx={{fontSize: 13, color: Grey7}}>Reading the offer…</Typography>
        </Box>;
    }

    const currency = totals?.Currency ?? terms?.Currency ?? null;
    const places = currency && minorUnits[currency] !== undefined ? minorUnits[currency] : 2;
    const hazardous = totals?.HazardousLines ?? 0;
    const unsourced = totals?.LinesWithNoCostSource ?? 0;

    return (
        <Box sx={{paddingTop: "6px"}}>

            {/* ── the terms the lines are priced on ─────────────────────────────────────────── */}
            <Box sx={{display: "flex", alignItems: "center", gap: "12px", marginBottom: "10px"}}>
                <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8}}>
                    Terms of this offer
                </Typography>
                <Box sx={{flex: 1}}/>
                {editable && (
                    <Button size="small" variant="outlined" onClick={() => setTermsDialog(true)}>
                        {needsTerms ? "Set the terms" : "Change the terms"}
                    </Button>
                )}
            </Box>

            {needsTerms ? (
                <Box sx={{border: `1px solid ${Grey2}`, borderRadius: "6px", padding: "14px 16px",
                          marginBottom: "18px"}}>
                    <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 680}}>
                        This revision has no terms yet, so it has no currency — and an offer with no
                        currency cannot have a total. Setting the currency is the first step in
                        pricing it; the margin, the overtime and a validity date can follow, or be
                        left off entirely.
                    </Typography>
                    {!editable && (
                        <Typography sx={{fontSize: 13, color: Grey5, marginTop: "8px"}}>
                            {readOnly
                                ? "This revision is not a draft, so its terms cannot be set here."
                                : "You do not hold the privilege to price this quote."}
                        </Typography>
                    )}
                </Box>
            ) : (
                <Box sx={{display: "flex", gap: "28px", flexWrap: "wrap",
                          border: `1px solid ${Grey2}`, borderRadius: "6px",
                          padding: "12px 16px", marginBottom: "18px"}}>
                    <Term label="Currency" value={currency ?? "—"}/>
                    <Term label="Margin"
                          value={terms?.MarginPercent != null ? `${terms.MarginPercent}%` : "none"}/>
                    <Term label="Overtime"
                          value={terms?.OvertimeCost != null
                                 ? money(terms.OvertimeCost, currency, places) : "none"}/>
                    <Term label="Valid until" value={when(terms?.ValidUntil)}/>
                </Box>
            )}

            {/* ── the lines ─────────────────────────────────────────────────────────────────── */}
            <Box sx={{display: "flex", alignItems: "center", gap: "12px", marginBottom: "10px"}}>
                <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8}}>
                    Lines
                </Typography>
                {!!lines?.length && (
                    <Typography sx={{fontSize: 13, color: Grey5}}>
                        {lines.length === 1 ? "1 line" : `${lines.length} lines`}
                    </Typography>
                )}
                <Box sx={{flex: 1}}/>
                {editable && !needsTerms && (
                    <Button size="small" variant="outlined" onClick={() => setLineDialog("new")}>
                        Add a line
                    </Button>
                )}
            </Box>

            {error && (
                <Typography sx={{fontSize: 13, color: "#B3261E", marginBottom: "10px"}}>
                    {error}
                </Typography>
            )}

            {/* THE FROZEN NOTICE CARRIES THE RECOVERY, which is the whole value of saying anything.
                "Released quotes are read-only" leaves a person stuck; naming Revise does not. */}
            {readOnly && !needsTerms && (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 680,
                                 marginBottom: "12px"}}>
                    This revision is {String(status ?? "").toLowerCase() || "not a draft"}, so its
                    lines are what the customer was sent and cannot be changed. To reprice it,
                    revise the quote — that makes a draft to work in, and this revision stays as it
                    went out.
                </Typography>
            )}

            {/* A HEADING OVER NOTHING READS AS A BROKEN PAGE. Lines cannot be added before the
                offer has a currency -- the service refuses them -- so this says that, rather than
                leaving the section blank and letting somebody hunt for the button. */}
            {needsTerms && (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 680}}>
                    Lines can be added once the offer has a currency. Every line is priced in it,
                    so there is nothing to price them in until the terms above are set.
                </Typography>
            )}

            {!error && !needsTerms && lines?.length === 0 && !editable && (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 680}}>
                    Nothing is on this offer yet. A line is a quantity of something at a price —
                    material, labour, freight, tooling or a one-off — and the total follows from
                    them and the terms above.
                </Typography>
            )}

            {(!!lines?.length || (editable && !needsTerms)) && (
                <Box sx={{border: `1px solid ${Grey2}`, borderRadius: "6px", overflowX: "auto"}}>
                    <Box sx={{display: "grid", gridTemplateColumns: COLUMNS, gap: "10px",
                              padding: "10px 14px", borderBottom: `1px solid ${Grey2}`,
                              background: "#FAFAFA", minWidth: 1040}}>
                        <Typography sx={headerCell}>Material</Typography>
                        <Typography sx={headerCell}>Description</Typography>
                        <Typography sx={{...headerCell, textAlign: "right"}}>Qty</Typography>
                        <Typography sx={headerCell}>Unit</Typography>
                        <Typography sx={{...headerCell, textAlign: "right"}}>Unit cost</Typography>
                        <Typography sx={{...headerCell, textAlign: "right"}}>Total</Typography>
                        <Typography sx={{...headerCell, textAlign: "right"}}>On-hand</Typography>
                        <Typography sx={headerCell}>Best supplier</Typography>
                        <Typography sx={{...headerCell, textAlign: "right"}}>Lead time</Typography>
                        <Typography sx={headerCell}/>
                    </Box>

                    {(lines ?? []).map(one => {
                        const onHand = one.Sourcing?.OnHand ?? null;
                        const best = one.Sourcing?.BestSupplier ?? null;
                        const lead = one.Sourcing?.LeadTimeDays ?? best?.LeadTimeDays ?? null;
                        return (
                        <Box key={one.USID}
                             sx={{display: "grid", gridTemplateColumns: COLUMNS, gap: "10px",
                                  padding: "9px 14px", alignItems: "center", minWidth: 1040,
                                  borderBottom: `1px solid ${Grey2}`,
                                  opacity: busy === one.USID ? 0.5 : 1}}>
                            {/* MATERIAL: the item's name over its number, or the fact that the
                                line is for nothing we hold. The hazard rides here as an icon. */}
                            <Box sx={{display: "flex", alignItems: "center", gap: "6px", minWidth: 0}}>
                                {one.Hazard && (
                                    <Tooltip title={one.HazardNote || "Marked hazardous"}>
                                        <WarningAmberOutlined
                                            sx={{fontSize: 16, color: "#B26A00", flexShrink: 0}}/>
                                    </Tooltip>
                                )}
                                <Box sx={{minWidth: 0}}>
                                    <Typography sx={{fontSize: 13, fontWeight: 600, color: Grey17,
                                                     overflow: "hidden", textOverflow: "ellipsis",
                                                     whiteSpace: "nowrap"}}
                                                title={one.ItemName ?? ""}>
                                        {one.ItemObjectUSID ? (one.ItemName || "Item") : "Manual entry"}
                                    </Typography>
                                    <Typography sx={{fontSize: 11, color: Grey6, whiteSpace: "nowrap"}}>
                                        {one.ItemObjectUSID ? (one.ItemObjectID || "—") : "not a catalogue item"}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography sx={{fontSize: 13, color: Grey17, overflow: "hidden",
                                             textOverflow: "ellipsis", whiteSpace: "nowrap"}}
                                        title={one.Description ?? ""}>
                                {one.Description || "—"}
                            </Typography>
                            <Typography sx={{fontSize: 13, color: Grey17, textAlign: "right",
                                             fontVariantNumeric: "tabular-nums"}}>
                                {quantity(one.Quantity)}
                            </Typography>
                            <Typography sx={{fontSize: 13, color: Grey7}}>
                                {one.UnitOfMeasure || "—"}
                            </Typography>
                            <Box sx={{textAlign: "right"}}>
                                <Typography sx={{fontSize: 13, color: Grey17,
                                                 fontVariantNumeric: "tabular-nums"}}>
                                    {money(one.UnitCost, null, places)}
                                </Typography>
                                {/* WHERE THE PRICE CAME FROM, under it, the way the source product
                                    badges "Std. cost". A price from nowhere is said out loud. */}
                                {one.CostSource
                                    ? <Chip size="small" label={one.CostSource.toLowerCase()}
                                            sx={{height: 16, fontSize: 10, "& .MuiChip-label": {px: 0.75}}}/>
                                    : <Typography sx={{fontSize: 10.5, color: "#B26A00"}}>no source</Typography>}
                            </Box>
                            <Typography sx={{fontSize: 13, color: Grey17, textAlign: "right",
                                             fontVariantNumeric: "tabular-nums"}}>
                                {money(one.ExtendedAmount, null, places)}
                            </Typography>
                            {/* ON-HAND: the lot ledger's sum for the item, or the reason there
                                is none. Never a zero that means "not read". */}
                            <Tooltip title={onHand?.Note ?? (onHand?.Lots ? `${onHand.Lots} lot${onHand.Lots === 1 ? "" : "s"}` : "")}>
                                <Typography sx={{fontSize: 13, textAlign: "right",
                                                 color: onHand?.Quantity != null ? Grey17 : Grey6,
                                                 fontVariantNumeric: "tabular-nums"}}>
                                    {onHand?.Quantity != null
                                        ? `${quantity(onHand.Quantity)}${onHand.UnitOfMeasure ? ` ${onHand.UnitOfMeasure}` : ""}`
                                        : "—"}
                                </Typography>
                            </Tooltip>
                            <Tooltip title={best
                                ? `${best.Approved ?? 1} approved supplier${(best.Approved ?? 1) === 1 ? "" : "s"}${best.UnitPrice?.Amount ? ` · ${best.UnitPrice.Amount} ${best.UnitPrice.Currency ?? ""}/unit` : ""}`
                                : (one.ItemObjectUSID ? "No approved supplier for this item." : "")}>
                                <Typography sx={{fontSize: 13, color: best ? Grey17 : Grey6,
                                                 overflow: "hidden", textOverflow: "ellipsis",
                                                 whiteSpace: "nowrap"}}>
                                    {best?.SupplierName ?? best?.SupplierObjectID ?? "—"}
                                </Typography>
                            </Tooltip>
                            <Typography sx={{fontSize: 13, textAlign: "right",
                                             color: lead != null ? Grey17 : Grey6}}>
                                {lead != null ? `${lead} d` : "—"}
                            </Typography>
                            <Box sx={{display: "flex", justifyContent: "flex-end"}}>
                                {editable && (
                                    <>
                                        <IconButton size="small" aria-label="Edit this line"
                                                    onClick={() => setLineDialog(one)}>
                                            <EditOutlined sx={{fontSize: 16}}/>
                                        </IconButton>
                                        <IconButton size="small" aria-label="Remove this line"
                                                    onClick={() => remove(one)}>
                                            <DeleteOutlined sx={{fontSize: 16}}/>
                                        </IconButton>
                                    </>
                                )}
                            </Box>
                        </Box>
                        );
                    })}

                    {/* THE ADD ROW, inline at the foot of the grid as the source product has it:
                        pick a catalogue item or type a material by hand, say how many at what,
                        press +. The dialog stays for editing a line and for the hazard. */}
                    {editable && !needsTerms && (
                        <AddLineRow revisionUSID={revisionUSID!} units={units}
                                    onAdded={() => load(true)}/>
                    )}
                </Box>
            )}

            {/* ── what it comes to ──────────────────────────────────────────────────────────── */}
            {totals && (
                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                          gap: "24px", marginTop: "14px"}}>
                    {/* THE TWO REVIEW FACTS SIT BESIDE THE MONEY, which is where somebody about to
                        approve a number is looking. Below the totals they read as a footnote to a
                        page that has already finished. */}
                    <Box sx={{display: "flex", flexDirection: "column", gap: "4px",
                              paddingTop: "3px"}}>
                        {hazardous > 0 && (
                            <Typography sx={{fontSize: 13, color: "#B26A00"}}>
                                {hazardous === 1 ? "1 line is hazardous"
                                                 : `${hazardous} lines are hazardous`}
                            </Typography>
                        )}
                        {unsourced > 0 && (
                            <Typography sx={{fontSize: 13, color: "#B26A00"}}>
                                {unsourced === 1
                                    ? "1 line has no cost source recorded"
                                    : `${unsourced} lines have no cost source recorded`}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{minWidth: 320}}>
                        <Amount label="Subtotal" value={money(totals.Subtotal, currency, places)}/>
                        {totals.MarginPercent != null && (
                            <Amount label={`Margin (${totals.MarginPercent}%)`}
                                    value={money(totals.MarginAmount, currency, places)}/>
                        )}
                        {totals.OvertimeCost != null && (
                            <Amount label="Overtime" value={money(totals.OvertimeCost, currency, places)}/>
                        )}
                        <Box sx={{borderTop: `1px solid ${Grey2}`, marginTop: "6px",
                                  paddingTop: "8px"}}>
                            <Amount label="Total" value={money(totals.Total, currency, places)} strong/>
                        </Box>
                        {/* THE ORDER OF OPERATIONS, SAID ONCE, where somebody looking at a margin
                            can see it. Margin applies to the line subtotal and overtime is added
                            after it -- the two orders differ by real money on any quote with
                            overtime, and nothing else on this page would tell a reviewer which
                            one produced the number they are approving. */}
                        {totals.MarginPercent != null && totals.OvertimeCost != null && (
                            <Typography sx={{fontSize: 12, color: Grey5, marginTop: "8px",
                                             lineHeight: 1.5}}>
                                The margin is applied to the line subtotal; overtime is added after
                                it.
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}

            {lineDialog && (
                <LineDialog line={lineDialog === "new" ? null : lineDialog}
                            revisionUSID={revisionUSID!}
                            currency={currency}
                            units={units}
                            onClose={() => setLineDialog(null)}
                            onSaved={() => { setLineDialog(null); load(true); }}/>
            )}

            {termsDialog && (
                <TermsDialog terms={needsTerms ? null : terms}
                             revisionUSID={revisionUSID!}
                             currencies={currencies}
                             onClose={() => setTermsDialog(false)}
                             onSaved={() => { setTermsDialog(false); loadTerms(); load(true); }}/>
            )}
        </Box>
    );
}

/**
 * The inline add row. "— Manual entry —" or a catalogue item through the Items list screen
 * (the same picker as everywhere else since this afternoon); the description defaults from the
 * item on the server when it is left blank, which is why it is optional here and not in the
 * dialog's words.
 */
function AddLineRow({revisionUSID, units, onAdded}: {
    revisionUSID: string; units: string[]; onAdded: () => void;
}) {
    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);
    const [item, setItem] = useState<string | null>(null);
    const [materialName, setMaterialName] = useState("");
    const [description, setDescription] = useState("");
    const [amount, setAmount] = useState("");
    const [unit, setUnit] = useState("");
    const [cost, setCost] = useState("");
    const [saving, setSaving] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    const ready = String(amount).trim() !== "" && String(cost).trim() !== "" && !!unit
                  && (!!item || materialName.trim() !== "" || description.trim() !== "");

    const add = async () => {
        setSaving(true); setProblem(null);
        try {
            await api.post("t1/v1/prepared_quote_lines", {
                PreparedQuoteRevisionUSID: revisionUSID,
                ItemObjectUSID: item || undefined,
                // A manual material with no description reads as the material's name.
                Description: (description || materialName).trim() || undefined,
                Quantity: amount, UnitOfMeasure: unit, UnitCost: cost,
                CostSource: item ? "CATALOGUE" : "MANUAL",
            });
            setItem(null); setMaterialName(""); setDescription(""); setAmount(""); setCost("");
            onAdded();
        } catch (caught: any) {
            setProblem(caught?.response?.data?.Cause ?? "That line could not be added.");
        } finally {
            setSaving(false);
        }
    };

    const small = {size: "small" as const, fullWidth: true,
                   slotProps: {input: {sx: {fontSize: 12.5, height: 32}}}};
    return (
        <>
            <Box sx={{display: "grid", gridTemplateColumns: COLUMNS, gap: "10px", padding: "8px 14px",
                      alignItems: "center", minWidth: 1040, background: "#FCFCFD"}}>
                <Box sx={{display: "flex", flexDirection: "column", gap: "4px"}}>
                    <FormObjectPicker label="Item" classificationCode="Item" options={[]} many={false}
                                      value={item} onChange={next => setItem(next ? String(next) : null)}/>
                    {!item && (
                        <TextField {...small} placeholder="Material name" value={materialName}
                                   onChange={event => setMaterialName(event.target.value)}/>
                    )}
                </Box>
                <TextField {...small} placeholder="Description" value={description}
                           onChange={event => setDescription(event.target.value)}/>
                <TextField {...small} placeholder="0" value={amount}
                           onChange={event => setAmount(event.target.value)}
                           slotProps={{input: {sx: {fontSize: 12.5, height: 32}, inputProps: {style: {textAlign: "right"}}}}}/>
                <TextField {...small} select value={unit} onChange={event => setUnit(event.target.value)}
                           slotProps={{input: {sx: {fontSize: 12.5, height: 32}}, select: {displayEmpty: true}}}>
                    <MenuItem value=""><span style={{color: Grey6}}>unit</span></MenuItem>
                    {units.map(one => <MenuItem key={one} value={one}>{one}</MenuItem>)}
                </TextField>
                <TextField {...small} placeholder="0.00" value={cost}
                           onChange={event => setCost(event.target.value)}
                           slotProps={{input: {sx: {fontSize: 12.5, height: 32}, inputProps: {style: {textAlign: "right"}}}}}/>
                <Typography sx={{fontSize: 12.5, color: Grey6, textAlign: "right"}}>
                    {amount && cost && Number.isFinite(Number(amount) * Number(cost))
                        ? (Number(amount) * Number(cost)).toFixed(2) : "—"}
                </Typography>
                <span/><span/><span/>
                <Box sx={{display: "flex", justifyContent: "flex-end"}}>
                    <IconButton size="small" aria-label="Add this line" disabled={!ready || saving}
                                onClick={add} sx={{color: Blue3}}>
                        <AddOutlined sx={{fontSize: 18}}/>
                    </IconButton>
                </Box>
            </Box>
            {problem && (
                <Typography sx={{fontSize: 12.5, color: "#B3261E", padding: "0 14px 8px"}}>{problem}</Typography>
            )}
        </>
    );
}

function Term({label, value}: {label: string; value: React.ReactNode}) {
    return (
        <Box>
            <Typography sx={{fontSize: 11, color: Grey5, textTransform: "uppercase",
                             letterSpacing: "0.04em"}}>
                {label}
            </Typography>
            <Typography sx={{fontSize: 13, color: Grey17, marginTop: "2px"}}>{value}</Typography>
        </Box>
    );
}

function Amount({label, value, strong}:
                {label: string; value: string; strong?: boolean}) {
    return (
        <Box sx={{display: "flex", justifyContent: "space-between", gap: "20px",
                  padding: "3px 0"}}>
            <Typography sx={{fontSize: 13, color: strong ? Grey17 : Grey7,
                             fontWeight: strong ? 600 : 400}}>
                {label}
            </Typography>
            <Typography sx={{fontSize: strong ? 15 : 13, color: strong ? Blue3 : Grey17,
                             fontWeight: strong ? 600 : 400,
                             fontVariantNumeric: "tabular-nums"}}>
                {value}
            </Typography>
        </Box>
    );
}

/**
 * One line, added or repriced.
 *
 * THE UNIT IS ALWAYS ASKED FOR, even when the line names an item. An item's own unit lives in its
 * form and cannot be read from here, and a material is often quoted in a unit it is not stocked
 * in -- the service says both and refuses a line without one.
 *
 * A ZERO UNIT COST IS ALLOWED AND A ZERO QUANTITY IS NOT, which is not an inconsistency: a line
 * included at no charge is a real line the customer should see, and a line for nothing of
 * something is a line that should have been deleted.
 */
function LineDialog({line, revisionUSID, currency, units, onClose, onSaved}: {
    line: Line | null; revisionUSID: string; currency?: string | null; units: string[];
    onClose: () => void; onSaved: () => void;
}) {
    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);

    const [description, setDescription] = useState(line?.Description ?? "");
    const [amount, setAmount] = useState(line?.Quantity ?? "");
    const [unit, setUnit] = useState(line?.UnitOfMeasure ?? "");
    const [cost, setCost] = useState(line?.UnitCost ?? "");
    const [source, setSource] = useState(line?.CostSource ?? "");
    const [hazard, setHazard] = useState(!!line?.Hazard);
    const [note, setNote] = useState(line?.HazardNote ?? "");
    const [saving, setSaving] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    const save = async () => {
        setSaving(true);
        setProblem(null);
        try {
            const body: Record<string, any> = {
                Description: description || undefined,
                Quantity: amount,
                UnitOfMeasure: unit,
                UnitCost: cost,
                CostSource: source || undefined,
                Hazard: hazard,
                HazardNote: hazard ? (note || undefined) : undefined,
            };
            if (line) {
                // THE CLEARS ARE SENT EXPLICITLY. An omitted parameter means "leave it alone", so
                // without these a hazard note once written could never be withdrawn and a cost
                // source could never be taken back off a line.
                await api.put("t1/v1/prepared_quote_lines", {
                    ...body, USID: line.USID,
                    ClearHazardNote: !hazard || !note ? true : undefined,
                    ClearCostSource: !source ? true : undefined,
                });
            } else {
                // NO SortOrder, DELIBERATELY. The service appends when it is absent; sending 0
                // would put every new line at the front of somebody's offer.
                await api.post("t1/v1/prepared_quote_lines",
                               {...body, PreparedQuoteRevisionUSID: revisionUSID});
            }
            onSaved();
        } catch (caught: any) {
            // THE SERVER'S OWN SENTENCE. Every refusal on this path is written to be read by the
            // person who hit it -- "a line for nothing of something", "above -100" -- and
            // replacing them with "could not be saved" throws away the only useful part.
            setProblem(caught?.response?.data?.Cause ?? "That line could not be saved.");
            setSaving(false);
        }
    };

    const ready = String(amount).trim() !== "" && String(cost).trim() !== "" && !!unit;

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{fontSize: 16}}>{line ? "Change this line" : "Add a line"}</DialogTitle>
            <DialogContent>
                <Box sx={{display: "flex", flexDirection: "column", gap: "14px", paddingTop: "4px"}}>
                    <TextField label="Description" size="small" fullWidth value={description}
                               onChange={event => setDescription(event.target.value)}
                               helperText="What the line says on the page."/>
                    <Box sx={{display: "flex", gap: "12px"}}>
                        <TextField label="Quantity" size="small" value={amount} sx={{flex: 1}}
                                   onChange={event => setAmount(event.target.value)}
                                   helperText="Above zero."/>
                        <TextField label="Unit" size="small" select value={unit} sx={{flex: 1}}
                                   onChange={event => setUnit(event.target.value)}
                                   helperText="Required, even for an item.">
                            {units.map(one => (
                                <MenuItem key={one} value={one}>{one}</MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <Box sx={{display: "flex", gap: "12px"}}>
                        <TextField label={currency ? `Unit cost (${currency})` : "Unit cost"}
                                   size="small" value={cost} sx={{flex: 1}}
                                   onChange={event => setCost(event.target.value)}
                                   helperText="Zero is allowed — a line at no charge."/>
                        <TextField label="Cost source" size="small" select value={source}
                                   sx={{flex: 1}}
                                   onChange={event => setSource(event.target.value)}
                                   helperText="Where the price came from.">
                            <MenuItem value="">Not recorded</MenuItem>
                            {COST_SOURCES.map(one => (
                                <MenuItem key={one} value={one}>{one}</MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <FormControlLabel
                        control={<Checkbox size="small" checked={hazard}
                                           onChange={event => setHazard(event.target.checked)}/>}
                        label={<Typography sx={{fontSize: 13}}>This line is hazardous</Typography>}/>
                    {/* THE NOTE NEEDS THE FLAG, which the service enforces -- a note without one
                        would print a warning on a line nothing says is dangerous. */}
                    {hazard && (
                        <TextField label="What the hazard is" size="small" fullWidth value={note}
                                   onChange={event => setNote(event.target.value)}/>
                    )}
                    {problem && (
                        <Typography sx={{fontSize: 13, color: "#B3261E", lineHeight: 1.5}}>
                            {problem}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={onClose}>Cancel</Button>
                <Button size="small" variant="contained" disabled={!ready || saving} onClick={save}>
                    {saving ? "Saving…" : line ? "Save" : "Add"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * The terms the arithmetic runs on.
 *
 * THE CURRENCY CAN BE SET AND NEVER CLEARED, which the service says too: every money answer depends
 * on it, and an offer whose currency was removed would have a column of numbers meaning nothing.
 *
 * EACH OF THE OTHER THREE HAS ITS OWN CLEAR, and emptying a field here sends it. Without that a
 * validity date once set could never be removed and the offer would go on claiming to lapse on a
 * date nobody wanted -- Phase 5's lesson, where a process step could not be moved back off a
 * machine.
 */
function TermsDialog({terms, revisionUSID, currencies, onClose, onSaved}: {
    terms: Terms | null; revisionUSID: string; currencies: string[];
    onClose: () => void; onSaved: () => void;
}) {
    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);

    const [currency, setCurrency] = useState(terms?.Currency ?? "");
    const [margin, setMargin] = useState(terms?.MarginPercent ?? "");
    const [overtime, setOvertime] = useState(terms?.OvertimeCost ?? "");
    const [validUntil, setValidUntil] = useState(terms?.ValidUntil ?? "");
    const [notes, setNotes] = useState(terms?.Notes ?? "");
    const [saving, setSaving] = useState(false);
    // THE REQUEST THIS OFFER ANSWERS (2026-10-28). An offer drafted from a project tab is not
    // linked to any request, so the Work Streams page could not place it; the terms are where
    // the link lives (QuoteRequestUSID), so the terms dialog is where it is set. The open
    // requests the persona can see are offered by number and subject.
    const [requests, setRequests] = useState<{USID: string; ObjectID?: string | null; Subject?: string | null}[]>([]);
    const [requestUSID, setRequestUSID] = useState<string>(terms?.QuoteRequestUSID ?? "");
    useEffect(() => {
        api.get("t1/v1/quote_requests", {params: {IncludeClosed: "true"},
                                         headers: {"idoc-response": "v1.QuoteRequest-001"}})
            .then((r: any) => {
                const held = r?.data?.PlatformMessage?.ListOfQuoteRequest?.QuoteRequest ?? [];
                setRequests(Array.isArray(held) ? held : [held]);
            })
            .catch(() => setRequests([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [problem, setProblem] = useState<string | null>(null);

    const save = async () => {
        setSaving(true);
        setProblem(null);
        try {
            await api.put("t1/v1/prepared_quote_terms", {
                PreparedQuoteRevisionUSID: revisionUSID,
                Currency: currency || undefined,
                MarginPercent: String(margin).trim() === "" ? undefined : margin,
                OvertimeCost: String(overtime).trim() === "" ? undefined : overtime,
                ValidUntil: validUntil || undefined,
                Notes: notes || undefined,
                QuoteRequestUSID: requestUSID || undefined,
                // AN EMPTIED FIELD IS A DECISION AND TRAVELS AS ONE. coalesce cannot express "set
                // to null", so the service takes a flag per nullable term.
                ClearMargin: String(margin).trim() === "" ? true : undefined,
                ClearOvertime: String(overtime).trim() === "" ? true : undefined,
                ClearValidUntil: !validUntil ? true : undefined,
            });
            onSaved();
        } catch (caught: any) {
            setProblem(caught?.response?.data?.Cause ?? "Those terms could not be saved.");
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{fontSize: 16}}>Terms of this offer</DialogTitle>
            <DialogContent>
                <Box sx={{display: "flex", flexDirection: "column", gap: "14px", paddingTop: "4px"}}>
                    <TextField label="Answers request" size="small" select fullWidth value={requestUSID}
                               onChange={event => setRequestUSID(event.target.value)}
                               helperText="The demand this offer answers. The work stream follows it.">
                        <MenuItem value=""><span style={{color: Grey6}}>None</span></MenuItem>
                        {requests.map(one => (
                            <MenuItem key={one.USID} value={one.USID}>
                                {one.ObjectID ?? "—"}{one.Subject ? ` · ${one.Subject}` : ""}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField label="Currency" size="small" select fullWidth value={currency}
                               onChange={event => setCurrency(event.target.value)}
                               helperText="Every amount on the offer is in this currency. It cannot
                                           be removed once set.">
                        {currencies.map(one => (
                            <MenuItem key={one} value={one}>{one}</MenuItem>
                        ))}
                    </TextField>
                    <Box sx={{display: "flex", gap: "12px"}}>
                        <TextField label="Margin %" size="small" value={margin} sx={{flex: 1}}
                                   onChange={event => setMargin(event.target.value)}
                                   helperText="Applied to the line subtotal. Leave empty for none."/>
                        <TextField label="Overtime" size="small" value={overtime} sx={{flex: 1}}
                                   onChange={event => setOvertime(event.target.value)}
                                   helperText="A flat addition, after the margin."/>
                    </Box>
                    <TextField label="Valid until" size="small" type="date" fullWidth
                               value={validUntil ? String(validUntil).slice(0, 10) : ""}
                               onChange={event => setValidUntil(event.target.value)}
                               slotProps={{inputLabel: {shrink: true}}}
                               helperText="When the offer lapses. Leave empty and it does not."/>
                    <TextField label="Internal notes" size="small" fullWidth multiline minRows={2}
                               value={notes}
                               onChange={event => setNotes(event.target.value)}
                               helperText="Not printed on the offer."/>
                    {problem && (
                        <Typography sx={{fontSize: 13, color: "#B3261E", lineHeight: 1.5}}>
                            {problem}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button size="small" onClick={onClose}>Cancel</Button>
                <Button size="small" variant="contained" disabled={!currency || saving}
                        onClick={save}>
                    {saving ? "Saving…" : "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
